/**
 * What a KPI is judged against, and where that number came from.
 *
 * The four constants in `~/components/contracts/constants` this replaces are mostly
 * guesses: the regular callback constant is 4 while the API falls back to 3, the
 * on-time constant is 80% where no contract agreed to one, and neither reflects the
 * per-elevator targets customers set on `PropertyContractCar`. Maintenance completion
 * at 100% is legitimate and stays — it is already normalised per elevator.
 *
 * The rule: use what the contract set, say where it came from, and where nothing was
 * set draw no verdict at all.
 */

/** The API's fallbacks for an elevator with no contract row, mirroring `services/api/main/models/contract.py`. */
export const DEFAULT_REGULAR_CALLBACK_RATE = 3;
export const DEFAULT_EMERGENCY_CALLBACK_RATE = 3;

export const FULL_MAINTENANCE_COMPLETION = 1;

export type TargetDirection = 'atLeast' | 'atMost';

export type ResolvedTarget = {
  /** Where a contract's elevators disagree this is the strictest of them. */
  value: number;
  values: readonly number[];
  source: 'cars' | 'contract';
};

/**
 * A target of 0 means "not set", not "zero tolerance": these columns are null or
 * defaulted to 0 by the migration that added them, and the per-elevator rates carry
 * `MinValueValidator(1)`.
 */
const isSet = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

/** The contract's on-time response target as the fraction the KPI reports, or null where it never set one. Stored as a percentage. */
export function resolveOnTimeTarget(pct: number | null | undefined): number | null {
  return isSet(pct) ? pct / 100 : null;
}

/**
 * A callback-rate target, taken from the elevators the contract covers where it can
 * and from the contract itself where it cannot. Null when neither is set.
 */
export function resolveCarRateTarget<TCar>(
  cars: readonly TCar[],
  pick: (car: TCar) => number | null | undefined,
  contractWide: number | null | undefined,
): ResolvedTarget | null {
  const fromCars = [...new Set(cars.map(pick).filter(isSet))].sort((a, b) => a - b);

  if (fromCars.length > 0) {
    return {
      value: fromCars[0],
      values: fromCars,
      source: 'cars',
    };
  }

  if (isSet(contractWide)) {
    return { value: contractWide, values: [contractWide], source: 'contract' };
  }

  return null;
}

/**
 * Whether a reading meets its target. `undefined` — no verdict — when either side is
 * missing: a KPI the period has no data for is an absence, and a target nobody set is
 * not something to be marked against.
 */
export function meetsTarget(
  value: number | null | undefined,
  target: ResolvedTarget | null,
  direction: TargetDirection,
): boolean | undefined {
  if (target === null) return undefined;
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
  return direction === 'atMost' ? value <= target.value : value >= target.value;
}

/** How a target reads on screen: "4", or "2–4" where the contract's elevators set different ones. */
export function formatTargetValues(
  target: ResolvedTarget,
  format: (value: number) => string,
): string {
  if (target.values.length === 1) return format(target.values[0]);
  return `${format(target.values[0])}–${format(target.values[target.values.length - 1])}`;
}
